import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { API_ORIGIN, identityAPI, userApprovalAPI, householdAPI } from '../../services/api';
import { toMarathiDigits } from '../../utils/marathi';
import RelationshipClaimReviewQueue from './RelationshipClaimReviewQueue';
import {
  BUSINESS_ROLE_FOR_RELATIONSHIP,
  EXTERNAL_BUSINESS_RELATIONSHIPS,
  IDENTITY_RELATIONSHIPS,
  buildCitizenApprovalPayload,
  buildExternalApprovalPayload,
  roleForSelectedParty,
  scopeLabel,
  uniqueParties,
  validateExternalApproval,
} from './userApprovalUi';
import './AdminUserApprovals.css';

const CITIZEN_RELATIONS = [
  { value: 'self', label: 'स्वतः (Self)' },
  { value: 'spouse', label: 'पती/पत्नी (Spouse)' },
  { value: 'son', label: 'मुलगा (Son)' },
  { value: 'daughter', label: 'मुलगी (Daughter)' },
  { value: 'father', label: 'वडील (Father)' },
  { value: 'mother', label: 'आई (Mother)' },
  { value: 'brother', label: 'भाऊ (Brother)' },
  { value: 'sister', label: 'बहीण (Sister)' },
  { value: 'other', label: 'इतर (Other)' },
];

export default function AdminUserApprovals() {
  const { tenant, user } = useAuth();
  const slug = tenant?.slug;

  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState({ pending: 0, approved: 0, rejected: 0, total: 0 });
  const [selectedUser, setSelectedUser] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [selectedBusinessId, setSelectedBusinessId] = useState('');
  const [selectedParty, setSelectedParty] = useState(null);
  const [partySearch, setPartySearch] = useState('');
  const [partyCandidates, setPartyCandidates] = useState([]);
  const [partySearchLoading, setPartySearchLoading] = useState(false);
  const [createNewParty, setCreateNewParty] = useState(false);
  const [identityRelationship, setIdentityRelationship] = useState('self');
  const [businessRelationship, setBusinessRelationship] = useState('owner');
  const [approvedScopes, setApprovedScopes] = useState([]);
  const [createBusinessRole, setCreateBusinessRole] = useState(false);
  const [ownershipShare, setOwnershipShare] = useState('');
  const [primaryOwner, setPrimaryOwner] = useState(false);
  const [approvalNote, setApprovalNote] = useState('');
  const [selectedHousehold, setSelectedHousehold] = useState(null);
  const [householdSearch, setHouseholdSearch] = useState('');
  const [householdCandidates, setHouseholdCandidates] = useState([]);
  const [householdSearchLoading, setHouseholdSearchLoading] = useState(false);
  const [householdPickerOpen, setHouseholdPickerOpen] = useState(false);
  const [citizenRelationship, setCitizenRelationship] = useState('other');
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [rejectNote, setRejectNote] = useState('');
  const [showRejectModal, setShowRejectModal] = useState(null);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({});
  const [pageSize, setPageSize] = useState(15);
  const [sort, setSort] = useState({ key: 'createdAt', direction: 'desc' });

  const loadUsers = useCallback(async () => {
    if (!slug) return;
    setLoading(true);
    try {
      const [usersRes, statsRes] = await Promise.all([
        userApprovalAPI.getAll(slug, { status: statusFilter, search, page, limit: pageSize, sort_by: sort.key, sort_order: sort.direction }),
        userApprovalAPI.getStats(slug),
      ]);
      setUsers(usersRes.data.users);
      setPagination(usersRes.data.pagination);
      setStats(statsRes.data.stats);
    } catch (err) {
      setError('Failed to load user registrations');
    } finally {
      setLoading(false);
    }
  }, [slug, statusFilter, search, page, pageSize, sort]);

  const changeSort = key => { setSort(current => ({ key, direction: current.key === key && current.direction === 'asc' ? 'desc' : 'asc' })); setPage(1); };
  const SortHeader = ({ column, children }) => <button className={`approval-sort-header ${sort.key === column ? 'active' : ''}`} onClick={() => changeSort(column)}>{children}<span>{sort.key === column ? (sort.direction === 'asc' ? '↑' : '↓') : '↕'}</span></button>;
  const exportCsv = async () => {
    try {
      const response = await userApprovalAPI.getAll(slug, { status: statusFilter, search, page: 1, limit: Math.max(pagination.total || 500, 500), sort_by: sort.key, sort_order: sort.direction });
      const cell = value => `"${String(value ?? '').replace(/"/g, '""')}"`;
      const rows = [['Name','Username','Email','Aadhaar','Phone','Status','Registered'], ...(response.data.users || []).map(u => [u.full_name,u.username,u.email,u.aadhaar_number,u.phone,u.approval_status,u.createdAt])];
      const link=document.createElement('a');link.href=URL.createObjectURL(new Blob(['\ufeff',rows.map(row=>row.map(cell).join(',')).join('\n')],{type:'text/csv;charset=utf-8'}));link.download=`user-approvals-${new Date().toISOString().slice(0,10)}.csv`;link.click();URL.revokeObjectURL(link.href);
    } catch (err) { setError('Export failed'); }
  };

  useEffect(() => { loadUsers(); }, [loadUsers]);

  const resetExternalDecision = (detail = null) => {
    const relationship = detail?.registration_metadata?.requested_business_relationship || 'owner';
    setSelectedParty(null);
    setPartySearch('');
    setPartyCandidates([]);
    setCreateNewParty(false);
    setIdentityRelationship('self');
    setBusinessRelationship(EXTERNAL_BUSINESS_RELATIONSHIPS.includes(relationship) ? relationship : 'owner');
    setApprovedScopes([]);
    setCreateBusinessRole(false);
    setOwnershipShare('');
    setPrimaryOwner(false);
    setApprovalNote('');
  };

  const openApplicant = async (user) => {
    setError('');
    setSelectedHousehold(null);
    setHouseholdSearch('');
    setHouseholdCandidates([]);
    setHouseholdPickerOpen(false);
    setCitizenRelationship('other');
    setApprovalNote('');
    if (user.approval_status !== 'pending') {
      setSelectedUser(user);
      setSelectedBusinessId('');
      resetExternalDecision();
      const reqHousehold = user.claim_review?.requested_household || null;
      setSelectedHousehold(reqHousehold);
      setHouseholdSearch(reqHousehold ? `${reqHousehold.household_uid} — ${reqHousehold.head_name}` : '');
      setCitizenRelationship(user.registration_metadata?.requested_relation_to_head || 'other');
      setDetailLoading(false);
      return;
    }
    setDetailLoading(true);
    setSelectedBusinessId('');
    resetExternalDecision();
    setSelectedUser(user);
    try {
      const response = await userApprovalAPI.getById(slug, user.id);
      const detail = response.data.user || response.data;
      setSelectedUser(detail);
      resetExternalDecision(detail);
      if (detail.registration_metadata?.account_kind === 'external_taxpayer') {
        const candidates = detail.claim_review?.business_candidates || [];
        const requested = detail.registration_metadata?.requested_business_id;
        const requestedCandidate = candidates.find(candidate => String(candidate.id) === String(requested));
        const businessUid = String(requestedCandidate?.business_uid || (candidates.length === 1 ? candidates[0].business_uid : ''));
        setSelectedBusinessId(businessUid);
        const selectedBusiness = candidates.find(candidate => candidate.business_uid === businessUid);
        setPartyCandidates(uniqueParties((selectedBusiness?.active_party_roles || []).map(role => role.party)));
      } else {
        const reqHousehold = detail.claim_review?.requested_household || null;
        setSelectedHousehold(reqHousehold);
        setHouseholdSearch(reqHousehold ? `${reqHousehold.household_uid} — ${reqHousehold.head_name}` : '');
        setCitizenRelationship(detail.registration_metadata?.requested_relation_to_head || 'other');
      }
    } catch (err) {
      setSelectedUser(null);
      setError(err.response?.data?.error || 'Failed to load the registration review');
    } finally {
      setDetailLoading(false);
    }
  };

  const handleHouseholdSearchChange = (value) => {
    setHouseholdSearch(value);
    setHouseholdPickerOpen(true);
    if (!value.trim()) {
      setSelectedHousehold(null);
    }
  };

  const selectHousehold = (household) => {
    if (!household) {
      setSelectedHousehold(null);
      setHouseholdSearch('');
      setHouseholdPickerOpen(false);
      setHouseholdCandidates([]);
      return;
    }
    setSelectedHousehold(household);
    setHouseholdSearch(`${household.household_uid} — ${household.head_name}`);
    setHouseholdPickerOpen(false);
    setHouseholdCandidates([]);
  };

  const toggleHouseholdPicker = async () => {
    const nextOpen = !householdPickerOpen;
    setHouseholdPickerOpen(nextOpen);
    if (nextOpen && !householdCandidates.length) {
      setHouseholdSearchLoading(true);
      try {
        const res = await householdAPI.getAll(slug, {
          search: householdSearch.trim() || undefined,
          page: 1,
          limit: 12,
          sort_by: 'head_name',
          sort_order: 'ASC',
        });
        setHouseholdCandidates(res.data.households || []);
      } catch (err) {
        setHouseholdCandidates([]);
      } finally {
        setHouseholdSearchLoading(false);
      }
    }
  };

  useEffect(() => {
    if (!slug || !selectedUser || selectedUser.registration_metadata?.account_kind === 'external_taxpayer') {
      return;
    }
    const query = householdSearch.trim();
    if (!query || (selectedHousehold && householdSearch === `${selectedHousehold.household_uid} — ${selectedHousehold.head_name}`)) {
      setHouseholdCandidates([]);
      setHouseholdSearchLoading(false);
      return;
    }

    const timer = setTimeout(async () => {
      setHouseholdSearchLoading(true);
      try {
        const res = await householdAPI.getAll(slug, {
          search: query,
          page: 1,
          limit: 12,
          sort_by: 'head_name',
          sort_order: 'ASC',
        });
        setHouseholdCandidates(res.data.households || []);
      } catch (err) {
        setHouseholdCandidates([]);
      } finally {
        setHouseholdSearchLoading(false);
      }
    }, 250);

    return () => clearTimeout(timer);
  }, [slug, selectedUser, householdSearch, selectedHousehold]);

  const searchParties = async () => {
    if (!partySearch.trim()) {
      setError('Enter a Party name, UID, phone, or email to search.');
      return;
    }
    setPartySearchLoading(true);
    setError('');
    try {
      const response = await identityAPI.searchParties(slug, {
        q: partySearch.trim(),
        party_status: 'active',
        profile_status: 'active',
        limit: 20,
      });
      setPartyCandidates(response.data.parties || []);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to search tenant Parties');
    } finally {
      setPartySearchLoading(false);
    }
  };

  const toggleApprovedScope = scope => {
    setApprovedScopes(current => current.includes(scope)
      ? current.filter(item => item !== scope)
      : [...current, scope]);
  };

  const selectBusiness = businessUid => {
    setSelectedBusinessId(businessUid);
    setSelectedParty(null);
    setCreateNewParty(false);
    setCreateBusinessRole(false);
    setOwnershipShare('');
    setPrimaryOwner(false);
    const business = selectedUser?.claim_review?.business_candidates?.find(candidate => candidate.business_uid === businessUid);
    setPartyCandidates(uniqueParties((business?.active_party_roles || []).map(role => role.party)));
  };

  const selectedBusiness = selectedUser?.claim_review?.business_candidates?.find(
    business => business.business_uid === selectedBusinessId
  ) || null;
  const existingBusinessRole = roleForSelectedParty({
    business: selectedBusiness,
    partyUid: selectedParty?.party_uid,
    relationship: businessRelationship,
  });
  const externalApprovalValues = {
    note: approvalNote,
    businessUid: selectedBusinessId,
    partyUid: selectedParty?.party_uid || '',
    partyType: selectedParty?.party_type || '',
    createNewParty,
    identityRelationship,
    businessRelationship,
    approvedScopes,
    createBusinessRole,
    existingRole: existingBusinessRole,
    businessVerified: selectedBusiness?.verification_status === 'verified',
    ownershipShare,
    isPrimary: primaryOwner,
  };
  const externalApprovalError = validateExternalApproval(externalApprovalValues);

  const handleApprove = async (userId) => {
    setActionLoading(true);
    setError('');
    try {
      const external = selectedUser?.registration_metadata?.account_kind === 'external_taxpayer';
      if (external && externalApprovalError) throw new Error(externalApprovalError);
      await userApprovalAPI.approve(slug, userId, external
        ? buildExternalApprovalPayload(externalApprovalValues)
        : buildCitizenApprovalPayload({
            note: approvalNote,
            householdId: selectedHousehold ? selectedHousehold.id : null,
            relationToHead: citizenRelationship,
          }));
      setSuccessMsg('User approved successfully');
      setSelectedUser(null);
      loadUsers();
      setTimeout(() => setSuccessMsg(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to approve user');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async (userId) => {
    if (!rejectNote.trim()) {
      setError('Please provide a reason for rejection');
      return;
    }
    setActionLoading(true);
    setError('');
    try {
      await userApprovalAPI.reject(slug, userId, { note: rejectNote });
      setSuccessMsg('User registration rejected');
      setShowRejectModal(null);
      setRejectNote('');
      setSelectedUser(null);
      loadUsers();
      setTimeout(() => setSuccessMsg(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to reject user');
    } finally {
      setActionLoading(false);
    }
  };

  const statusBadge = (status) => {
    const styles = {
      pending: { background: '#fff3e0', color: '#e65100', border: '1px solid #ffcc80' },
      approved: { background: '#e8f5e9', color: '#2e7d32', border: '1px solid #a5d6a7' },
      rejected: { background: '#ffebee', color: '#c62828', border: '1px solid #ef9a9a' },
    };
    return (
      <span style={{
        ...styles[status],
        padding: '3px 10px',
        borderRadius: 12,
        fontSize: 12,
        fontWeight: 600,
        textTransform: 'uppercase',
      }}>
        {status}
      </span>
    );
  };

  return (
    <div>
      <h2 style={{ marginBottom: 4 }}>👥 नागरिक नोंदणी मंजुरी / User Registration Approvals</h2>
      <p style={{ color: '#666', marginBottom: 20 }}>
        Review and approve citizen registrations for your Grampanchayat
      </p>

      <RelationshipClaimReviewQueue tenantSlug={slug} user={user} />

      {/* Stats Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 16, marginBottom: 24 }}>
        {[
          { label: 'Pending', value: stats.pending, color: '#ff9800', icon: '⏳' },
          { label: 'Approved', value: stats.approved, color: '#4caf50', icon: '✅' },
          { label: 'Rejected', value: stats.rejected, color: '#f44336', icon: '❌' },
          { label: 'Total', value: stats.total, color: '#2196f3', icon: '👥' },
        ].map(s => (
          <div key={s.label} className="card" style={{
            padding: '16px', textAlign: 'center', borderTop: `3px solid ${s.color}`,
          }}>
            <div style={{ fontSize: 24 }}>{s.icon}</div>
            <div style={{ fontSize: 28, fontWeight: 700, color: s.color }}>{s.value}</div>
            <div style={{ fontSize: 13, color: '#666' }}>{s.label}</div>
          </div>
        ))}
      </div>

      {successMsg && <div className="alert alert-success" style={{ marginBottom: 16 }}>{successMsg}</div>}
      {error && <div className="alert alert-error" style={{ marginBottom: 16 }}>{error}</div>}

      {/* Filters */}
      <div className="card approval-toolbar">
        <div>
          <label style={{ fontSize: 13, fontWeight: 600, marginRight: 8 }}>Status:</label>
          {[
            { key: 'all', label: 'All', count: stats.total },
            { key: 'pending', label: 'Pending', count: stats.pending },
            { key: 'approved', label: 'Approved', count: stats.approved },
            { key: 'rejected', label: 'Rejected', count: stats.rejected },
          ].map(option => (
            <button
              key={option.key}
              onClick={() => { setStatusFilter(option.key); setPage(1); }}
              className={`btn btn-sm approval-status-filter ${statusFilter === option.key ? 'btn-primary active' : 'btn-outline'}`}
              aria-pressed={statusFilter === option.key}
            >
              <span>{option.label}</span>
              <span className="approval-filter-count">{option.count || 0}</span>
            </button>
          ))}
        </div>
        <div style={{ flex: 1, minWidth: 200 }}>
          <input
            type="text" placeholder="Search by name, email, Aadhaar, phone..."
            value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            style={{ width: '100%' }}
          />
        </div>
        <button className="approval-export-btn" onClick={exportCsv}>⇩ CSV Export</button>
      </div>

      {/* User List Table */}
      {loading && users.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 40, color: '#888' }}>Loading...</div>
      ) : users.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: 40 }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>📋</div>
          <p style={{ color: '#888' }}>No {statusFilter !== 'all' ? statusFilter : ''} registrations found</p>
        </div>
      ) : (
        <>
          <div className={`card approval-table-scroll ${loading ? 'is-refreshing' : ''}`}>
            <table className="approval-data-table">
              <thead>
                <tr>
                  <th><SortHeader column="full_name">Name</SortHeader></th>
                  <th><SortHeader column="email">Email</SortHeader></th>
                  <th style={thStyle}>Aadhaar</th>
                  <th><SortHeader column="phone">Phone</SortHeader></th>
                  <th><SortHeader column="approval_status">Status</SortHeader></th>
                  <th><SortHeader column="createdAt">Registered</SortHeader></th>
                  <th style={thStyle}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map(u => (
                  <tr key={u.id}>
                    <td>
                      <strong>{u.full_name}</strong>
                      <br /><small style={{ color: '#888' }}>@{u.username}</small>
                    </td>
                    <td>{u.email}</td>
                    <td>
                      <code style={{ fontSize: 13 }}>
                        {u.aadhaar_number
                          ? `${u.aadhaar_number.slice(0, 4)} **** ${u.aadhaar_number.slice(8)}`
                          : '-'}
                      </code>
                    </td>
                    <td>{u.phone || '-'}</td>
                    <td>{statusBadge(u.approval_status)}</td>
                    <td>
                      {toMarathiDigits(new Date(u.createdAt).toLocaleDateString('mr-IN', {
                        day: '2-digit', month: 'short', year: 'numeric',
                      }))}
                    </td>
                    <td>
                      <button
                        className="btn btn-sm btn-outline"
                        onClick={() => openApplicant(u)}
                        style={{ marginRight: 4 }}
                      >
                        👁️ View
                      </button>
                      {u.approval_status === 'pending' && (
                        <button
                          className="btn btn-sm btn-primary"
                          onClick={() => openApplicant(u)}
                          disabled={actionLoading}
                        >
                          Review decision
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <div className="table-pagination approval-pagination">
              <label>Per page <select value={pageSize} onChange={e => { setPageSize(Number(e.target.value)); setPage(1); }}><option>10</option><option>15</option><option>25</option><option>50</option></select></label>
              <button className="btn btn-sm btn-outline" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>
                ← Previous
              </button>
              <span style={{ alignSelf: 'center', fontSize: 14, color: '#666' }}>
                Page {page} of {pagination.totalPages}
              </span>
              <button className="btn btn-sm btn-outline" disabled={page >= pagination.totalPages} onClick={() => setPage(p => p + 1)}>
                Next →
              </button>
            </div>
          )}
        </>
      )}

      {/* User Detail Modal */}
      {selectedUser && (
        <div style={overlayStyle} onClick={() => setSelectedUser(null)}>
          <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <h3 style={{ margin: 0 }}>👤 User Details</h3>
              <button onClick={() => setSelectedUser(null)} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer' }}>✕</button>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 }}>
              <DetailField label="Full Name" value={selectedUser.full_name} />
              <DetailField label="Username" value={selectedUser.username} />
              <DetailField label="Email" value={selectedUser.email} />
              <DetailField label="Phone" value={selectedUser.phone || '-'} />
              <DetailField
                label="Aadhaar Number"
                value={selectedUser.aadhaar_number
                  ? `XXXX XXXX ${String(selectedUser.aadhaar_number).slice(-4)}`
                  : '-'}
              />
              <DetailField label="Status" value={statusBadge(selectedUser.approval_status)} isComponent />
              <DetailField label="Registered On" value={toMarathiDigits(new Date(selectedUser.createdAt).toLocaleString('mr-IN'))} />
              {selectedUser.approved_at && (
                <DetailField label="Reviewed On" value={toMarathiDigits(new Date(selectedUser.approved_at).toLocaleString('mr-IN'))} />
              )}
            </div>

            {detailLoading && (
              <div style={{ padding: 12, marginBottom: 16, background: '#eef6ff', borderRadius: 8 }}>
                Loading the household and identity claim for review…
              </div>
            )}

            {!detailLoading && selectedUser.claim_review && (
              <section style={{ padding: 14, marginBottom: 18, border: '1px solid #bbdefb', borderRadius: 8, background: '#f7fbff' }}>
                <h4 style={{ margin: '0 0 12px' }}>Registration claim review</h4>
                {selectedUser.registration_metadata?.account_kind === 'external_taxpayer' ? (
                  <div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
                      <DetailField label="Account type" value="External business taxpayer" />
                      <DetailField
                        label="Requested relationship"
                        value={selectedUser.registration_metadata?.requested_business_relationship || 'owner'}
                      />
                      <DetailField
                        label="Business supplied by applicant"
                        value={selectedUser.registration_metadata?.business_name
                          || selectedUser.registration_metadata?.business_registration_number
                          || selectedUser.registration_metadata?.requested_business_id
                          || 'Not specified'}
                      />
                      <DetailField
                        label="External address"
                        value={selectedUser.registration_metadata?.external_address || 'Not provided'}
                      />
                    </div>
                    <label htmlFor="approval-business" style={{ display: 'block', fontWeight: 600, marginBottom: 6 }}>
                      Link to the verified Business record *
                    </label>
                    <select
                      id="approval-business"
                      value={selectedBusinessId}
                      onChange={event => selectBusiness(event.target.value)}
                      style={{ width: '100%', padding: 9, border: '1px solid #90a4ae', borderRadius: 6 }}
                    >
                      <option value="">Select the exact business</option>
                      {(selectedUser.claim_review.business_candidates || []).map(business => (
                        <option key={business.business_uid} value={business.business_uid}>
                          {business.business_name} — {business.registration_number || 'no registration number'}
                        </option>
                      ))}
                    </select>
                    {!(selectedUser.claim_review.business_candidates || []).length && (
                      <div style={{ padding: 10, marginTop: 8, border: '1px solid #f3c56d', borderRadius: 8, background: '#fff8e8', color: '#8a5508', fontSize: 13 }}>
                        <strong>व्यवसाय नोंदवहीत अचूक नोंद उपलब्ध नाही.</strong>
                        <p style={{ margin: '4px 0 8px' }}>अर्जातील माहिती वापरून आधी व्यवसायाचा मसुदा तयार करा, कागदपत्रे पडताळा आणि नंतर हा अर्ज पुन्हा उघडून अचूक व्यवसाय निवडा.</p>
                        <a
                          className="btn btn-sm btn-outline"
                          target="_blank"
                          rel="noopener noreferrer"
                          href={`/admin/${slug}/businesses/new?${new URLSearchParams({
                            business_name: selectedUser.registration_metadata?.business_name || '',
                            owner_name: selectedUser.full_name || '',
                            phone: selectedUser.phone || '',
                            email: selectedUser.email || '',
                            business_type: selectedUser.registration_metadata?.business_type || 'other',
                            address: selectedUser.registration_metadata?.business_address || '',
                            registration_number: selectedUser.registration_metadata?.business_registration_number || '',
                            commenced_on: selectedUser.registration_metadata?.business_commenced_on || '',
                            source_user_id: String(selectedUser.id || ''),
                          }).toString()}`}
                        >
                          व्यवसाय मसुदा तयार करा ↗
                        </a>
                      </div>
                    )}

                    <div className="external-approval-section">
                      <h5>1. Link the applicant to the canonical Party</h5>
                      <p>Search before creating. Active Business owners are listed first when available.</p>
                      <div className="external-party-search">
                        <input
                          value={partySearch}
                          onChange={event => setPartySearch(event.target.value)}
                          placeholder="Party name, stable UID, phone, or email"
                        />
                        <button type="button" className="btn btn-sm btn-outline" onClick={searchParties} disabled={partySearchLoading}>
                          {partySearchLoading ? 'Searching…' : 'Search Parties'}
                        </button>
                      </div>
                      {!!partyCandidates.length && <div className="external-party-results">
                        {partyCandidates.map(party => {
                          const role = (selectedBusiness?.active_party_roles || []).find(item => item.party?.party_uid === party.party_uid);
                          return <button
                            type="button"
                            key={party.party_uid}
                            className={selectedParty?.party_uid === party.party_uid ? 'selected' : ''}
                            onClick={() => {
                              setSelectedParty(party);
                              setCreateNewParty(false);
                              setCreateBusinessRole(false);
                              setOwnershipShare('');
                              setPrimaryOwner(false);
                            }}
                          >
                            <strong>{party.display_name || party.legal_name}</strong>
                            <small>{party.tenant_profile?.local_party_uid || party.party_uid}</small>
                            <span>{party.tenant_profile?.profile_kind || party.party_type}{role ? ` · current ${role.role_type}` : ''}</span>
                          </button>;
                        })}
                      </div>}
                      {selectedParty && <div className="external-selected-party">
                        <strong>Selected existing Party: {selectedParty.display_name || selectedParty.legal_name}</strong>
                        <span>{selectedParty.party_uid} · KYC {selectedParty.tenant_profile?.kyc_status || 'unknown'}</span>
                      </div>}
                      <label className="external-check-row">
                        <input
                          type="checkbox"
                          checked={createNewParty}
                          onChange={event => {
                            setCreateNewParty(event.target.checked);
                            if (event.target.checked) setSelectedParty(null);
                            setCreateBusinessRole(false);
                          }}
                        />
                        <span><strong>No matching Party exists</strong><small>Create a new individual Party from this reviewed registration. Obvious phone/email duplicates are blocked by the server.</small></span>
                      </label>
                      <label className="external-select-field">Exact user-to-Party identity relationship
                        <select value={identityRelationship} onChange={event => setIdentityRelationship(event.target.value)}>
                          {IDENTITY_RELATIONSHIPS.map(value => <option key={value} value={value}>{value.replaceAll('_', ' ')}</option>)}
                        </select>
                      </label>
                    </div>

                    <div className="external-approval-section">
                      <h5>2. Confirm the Business relationship</h5>
                      <label className="external-select-field">Approved relationship
                        <select value={businessRelationship} onChange={event => {
                          setBusinessRelationship(event.target.value);
                          setCreateBusinessRole(false);
                          setOwnershipShare('');
                          setPrimaryOwner(false);
                        }}>
                          {EXTERNAL_BUSINESS_RELATIONSHIPS.map(value => <option key={value} value={value}>{value.replaceAll('_', ' ')}</option>)}
                        </select>
                      </label>
                      {BUSINESS_ROLE_FOR_RELATIONSHIP[businessRelationship] ? (
                        existingBusinessRole ? <div className="external-role-reuse">
                          <strong>Reuse current {existingBusinessRole.role_type} role</strong>
                          <span>{existingBusinessRole.ownership_share == null ? 'No recorded share' : `${existingBusinessRole.ownership_share}% share`} · {existingBusinessRole.is_primary ? 'primary' : 'not primary'} · no registry mutation</span>
                        </div> : <>
                          <label className="external-check-row">
                            <input
                              type="checkbox"
                              checked={createBusinessRole}
                              onChange={event => setCreateBusinessRole(event.target.checked)}
                            />
                            <span><strong>Create the missing {BUSINESS_ROLE_FOR_RELATIONSHIP[businessRelationship]} role</strong><small>Use only after checking the registry and evidence. Verified Businesses require the separate role-change approval workflow.</small></span>
                          </label>
                          {createBusinessRole && <div className="external-role-fields">
                            {['owner', 'partner'].includes(businessRelationship) && <label>Exact ownership share %
                              <input type="number" min="0.0001" max="100" step="0.0001" value={ownershipShare} onChange={event => setOwnershipShare(event.target.value)} />
                            </label>}
                            <label className="external-check-row compact">
                              <input type="checkbox" checked={primaryOwner} onChange={event => setPrimaryOwner(event.target.checked)} />
                              <span>Primary liable Party</span>
                            </label>
                          </div>}
                          {selectedBusiness?.verification_status === 'verified' && <p className="external-warning">
                            This verified Business has no matching current role. Add/approve the role through the Business registry change workflow, then return here and reuse it.
                          </p>}
                        </>
                      ) : <div className="external-role-reuse">
                        <strong>No ownership role required</strong>
                        <span>This is an access-only relationship; the account grant remains limited to the reviewed scopes.</span>
                      </div>}
                    </div>

                    <div className="external-approval-section">
                      <h5>3. Review note & permissions</h5>
                      <p style={{ color: '#059669', fontWeight: 600, margin: '4px 0 12px' }}>
                        ✓ सर्व आवश्यक परवानग्या (View, Pay, Receipts, Appeals) मंजुरीनंतर आपोआप लागू होतील.
                      </p>
                      <label className="external-select-field">Evidence review note *
                        <textarea
                          rows="3"
                          value={approvalNote}
                          onChange={event => setApprovalNote(event.target.value)}
                          placeholder="Record the identity, Party, and relationship evidence reviewed"
                        />
                      </label>
                    </div>

                    <p style={{ margin: '12px 0 0', color: '#455a64', fontSize: 13 }}>
                      Approval creates an external taxpayer membership with full access permissions. It never creates a village Citizen profile.
                    </p>
                  </div>
                ) : (
                  <div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
                      <DetailField
                        label="Requested relationship"
                        value={selectedUser.registration_metadata?.requested_relation_to_head || 'Not specified'}
                      />
                      <DetailField
                        label="Requested household"
                        value={selectedUser.claim_review.requested_household
                          ? `${selectedUser.claim_review.requested_household.household_uid} — ${selectedUser.claim_review.requested_household.head_name}`
                          : 'No household requested'}
                      />
                      {selectedUser.claim_review.requested_household && (
                        <DetailField
                          label="Household address"
                          value={`${selectedUser.claim_review.requested_household.address || '-'}${selectedUser.claim_review.requested_household.ward_number ? `, Ward ${selectedUser.claim_review.requested_household.ward_number}` : ''}`}
                        />
                      )}
                      <DetailField
                        label="Existing citizen candidate"
                        value={selectedUser.claim_review.candidate_citizen
                          ? `${selectedUser.claim_review.candidate_citizen.full_name} (${selectedUser.claim_review.candidate_citizen.phone || selectedUser.claim_review.candidate_citizen.email || 'no contact'})`
                          : 'No existing citizen profile matched'}
                      />
                    </div>

                    {selectedUser.approval_status === 'pending' ? (
                      <div className="citizen-household-review-section">
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8, flexWrap: 'wrap', gap: 6 }}>
                          <label style={{ fontWeight: 700, fontSize: 13, color: '#1565c0', margin: 0 }}>
                            🏠 Verified Household Link *
                          </label>
                          {selectedHousehold && selectedUser.claim_review?.requested_household && (
                            selectedHousehold.id !== selectedUser.claim_review.requested_household.id ? (
                              <span style={{ fontSize: 11, background: '#fff3cd', color: '#856404', padding: '2px 8px', borderRadius: 4, fontWeight: 600 }}>
                                ✏️ Corrected by Admin
                              </span>
                            ) : (
                              <span style={{ fontSize: 11, background: '#d4edda', color: '#155724', padding: '2px 8px', borderRadius: 4, fontWeight: 600 }}>
                                ✓ Matches Applicant Request
                              </span>
                            )
                          )}
                          {!selectedUser.claim_review?.requested_household && selectedHousehold && (
                            <span style={{ fontSize: 11, background: '#cce5ff', color: '#004085', padding: '2px 8px', borderRadius: 4, fontWeight: 600 }}>
                              + Added by Admin
                            </span>
                          )}
                          {selectedUser.claim_review?.requested_household && !selectedHousehold && (
                            <span style={{ fontSize: 11, background: '#f8d7da', color: '#721c24', padding: '2px 8px', borderRadius: 4, fontWeight: 600 }}>
                              ⚠️ Unlinked by Admin
                            </span>
                          )}
                        </div>

                        {/* Searchable Combobox */}
                        <div className="reg-household-combobox" style={{ marginBottom: 12 }}>
                          <div className="reg-household-field">
                            <input
                              type="text"
                              role="combobox"
                              aria-expanded={householdPickerOpen}
                              value={householdSearch}
                              onChange={(e) => handleHouseholdSearchChange(e.target.value)}
                              onFocus={() => setHouseholdPickerOpen(true)}
                              placeholder="कुटुंबप्रमुखाचे नाव, UID किंवा मोबाईल शोधा (Search name, UID, or phone)"
                              autoComplete="off"
                              style={{ width: '100%', padding: '9px 75px 9px 12px', border: '1px solid #90a4ae', borderRadius: 6, fontSize: 13 }}
                            />
                            {householdSearch && (
                              <button
                                type="button"
                                className="reg-household-clear"
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={() => selectHousehold(null)}
                                title="Clear selection"
                              >
                                ×
                              </button>
                            )}
                            <button
                              type="button"
                              className="reg-household-toggle"
                              onMouseDown={(e) => e.preventDefault()}
                              onClick={toggleHouseholdPicker}
                              title="Toggle options"
                            >
                              ⌄
                            </button>
                          </div>

                          {householdPickerOpen && (
                            <div className="reg-household-menu" role="listbox">
                              <button
                                type="button"
                                role="option"
                                aria-selected={!selectedHousehold}
                                className="reg-household-option none"
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={() => selectHousehold(null)}
                              >
                                <strong>❌ कोणतेही कुटुंब लिंक करू नका (No Household Linked)</strong>
                                <span>सध्या कुटुंब लिंक न करता केवळ नागरिक खाते मंजूर करा.</span>
                              </button>

                              {householdSearchLoading ? (
                                <div className="reg-household-empty">कुटुंब शोधत आहे… (Searching…)</div>
                              ) : householdCandidates.length > 0 ? (
                                householdCandidates.map((h) => (
                                  <button
                                    key={h.id}
                                    type="button"
                                    role="option"
                                    aria-selected={selectedHousehold && selectedHousehold.id === h.id}
                                    className="reg-household-option"
                                    onMouseDown={(e) => e.preventDefault()}
                                    onClick={() => selectHousehold(h)}
                                  >
                                    <strong>{h.household_uid} — {h.head_name}</strong>
                                    <span>
                                      {[
                                        h.ward_number ? `प्रभाग ${h.ward_number}` : '',
                                        h.address ? h.address : '',
                                        h.total_members ? `सदस्य: ${h.total_members}` : '',
                                      ].filter(Boolean).join(' • ')}
                                    </span>
                                  </button>
                                ))
                              ) : householdSearch.trim() ? (
                                <div className="reg-household-empty">या नावाने किंवा UID ने कोणतेही कुटुंब आढळले नाही.</div>
                              ) : (
                                <div className="reg-household-empty">शोधण्यासाठी नाव, UID किंवा मोबाईल टाइप करा.</div>
                              )}
                            </div>
                          )}
                        </div>

                        {/* Selected Household Card Preview */}
                        {selectedHousehold && (
                          <div style={{ background: '#f0f7ff', border: '1px solid #c8e1ff', borderRadius: 6, padding: '10px 14px', marginBottom: 14 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                              <div>
                                <strong style={{ color: '#0d47a1', fontSize: 14 }}>
                                  🏠 {selectedHousehold.household_uid} — {selectedHousehold.head_name}
                                </strong>
                                <div style={{ fontSize: 12, color: '#455a64', marginTop: 3 }}>
                                  {[
                                    selectedHousehold.ward_number ? `प्रभाग (Ward) ${selectedHousehold.ward_number}` : '',
                                    selectedHousehold.address ? `पत्ता: ${selectedHousehold.address}` : '',
                                    selectedHousehold.total_members ? `एकूण सदस्य: ${selectedHousehold.total_members}` : '',
                                  ].filter(Boolean).join(' | ')}
                                </div>
                              </div>
                              <button
                                type="button"
                                onClick={() => selectHousehold(null)}
                                style={{ background: 'none', border: 'none', color: '#c62828', fontSize: 12, cursor: 'pointer', textDecoration: 'underline' }}
                              >
                                काढा (Remove)
                              </button>
                            </div>
                          </div>
                        )}

                        {/* Relationship to Head and Note */}
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
                          <div>
                            <label htmlFor="approval-citizen-relationship" style={{ display: 'block', fontWeight: 600, fontSize: 12, marginBottom: 4 }}>
                              कुटुंबप्रमुखाशी नाते (Relationship to Head) *
                            </label>
                            <select
                              id="approval-citizen-relationship"
                              value={citizenRelationship}
                              onChange={(e) => setCitizenRelationship(e.target.value)}
                              style={{ width: '100%', padding: '8px 10px', border: '1px solid #90a4ae', borderRadius: 6, fontSize: 13 }}
                            >
                              {CITIZEN_RELATIONS.map((rel) => (
                                <option key={rel.value} value={rel.value}>{rel.label}</option>
                              ))}
                            </select>
                          </div>
                          <div>
                            <label htmlFor="approval-review-note" style={{ display: 'block', fontWeight: 600, fontSize: 12, marginBottom: 4 }}>
                              Review Note (ऐच्छिक / Optional)
                            </label>
                            <input
                              id="approval-review-note"
                              type="text"
                              value={approvalNote}
                              onChange={(e) => setApprovalNote(e.target.value)}
                              placeholder="उदा. कागदपत्र पडताळणी / कुटुंब दुरुस्ती..."
                              style={{ width: '100%', padding: '8px 10px', border: '1px solid #90a4ae', borderRadius: 6, fontSize: 13 }}
                            />
                          </div>
                        </div>

                        <p style={{ margin: '8px 0 0', color: '#455a64', fontSize: 12, lineHeight: 1.4 }}>
                          Approval will activate this claim, link or correct the verified household, and synchronize citizen profile and household member counts.
                        </p>
                      </div>
                    ) : (
                      <div>
                        {selectedUser.registration_metadata?.approved_household_id && (
                          <div style={{ padding: 10, background: '#e8f5e9', border: '1px solid #c8e6c9', borderRadius: 6, marginTop: 8, fontSize: 13 }}>
                            <strong>मंजूर केलेले कुटुंब (Approved Household ID):</strong> #{selectedUser.registration_metadata.approved_household_id}
                            {selectedUser.registration_metadata?.approved_relation_to_head && (
                              <span> • नाते: {selectedUser.registration_metadata.approved_relation_to_head}</span>
                            )}
                            {selectedUser.registration_metadata?.household_corrected_by_admin && (
                              <span style={{ marginLeft: 8, color: '#e65100', fontWeight: 600 }}>(प्रशासकाद्वारे दुरुस्त)</span>
                            )}
                          </div>
                        )}
                        <p style={{ margin: '12px 0 0', color: '#455a64', fontSize: 13 }}>
                          Approval activated this claim and synchronized the citizen profile.
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </section>
            )}

            {selectedUser.approval_note && (
              <div style={{ background: '#f5f5f5', padding: 12, borderRadius: 8, marginBottom: 16 }}>
                <strong>Admin Note:</strong> {selectedUser.approval_note}
              </div>
            )}

            {/* Aadhaar Photo */}
            {selectedUser.aadhaar_photo_url && (
              <div style={{ marginBottom: 20 }}>
                <h4 style={{ marginBottom: 8 }}>🪪 Aadhaar Card Photo</h4>
                <div style={{ border: '1px solid #e0e0e0', borderRadius: 8, padding: 8, textAlign: 'center', background: '#fafafa' }}>
                  {selectedUser.aadhaar_photo_url.endsWith('.pdf') ? (
                    <div style={{ padding: 20 }}>
                      <span style={{ fontSize: 40 }}>📄</span>
                      <p>PDF Document</p>
                      <a
                        href={`${API_ORIGIN}${selectedUser.aadhaar_photo_url}`}
                        target="_blank" rel="noopener noreferrer"
                        className="btn btn-sm btn-outline"
                      >
                        Open PDF
                      </a>
                    </div>
                  ) : (
                    <img
                      src={`${API_ORIGIN}${selectedUser.aadhaar_photo_url}`}
                      alt="Aadhaar Card"
                      style={{ maxWidth: '100%', maxHeight: 350, borderRadius: 4 }}
                    />
                  )}
                </div>
              </div>
            )}

            {/* Action Buttons */}
            {selectedUser.approval_status === 'pending' && (
              <>
              {selectedUser.registration_metadata?.account_kind === 'external_taxpayer' && externalApprovalError && (
                <div className="external-decision-error">{externalApprovalError}</div>
              )}
              <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
                <button
                  className="btn btn-primary"
                  onClick={() => handleApprove(selectedUser.id)}
                  disabled={actionLoading || detailLoading
                    || (selectedUser.registration_metadata?.account_kind === 'external_taxpayer' && Boolean(externalApprovalError))}
                >
                  ✅ Approve User
                </button>
                <button
                  className="btn"
                  onClick={() => { setShowRejectModal(selectedUser.id); setRejectNote(''); }}
                  style={{ background: '#f44336', color: '#fff', border: 'none' }}
                >
                  ❌ Reject User
                </button>
              </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Reject Modal */}
      {showRejectModal && (
        <div style={overlayStyle} onClick={() => setShowRejectModal(null)}>
          <div style={{ ...modalStyle, maxWidth: 450 }} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ marginBottom: 16, color: '#c62828' }}>❌ Reject Registration</h3>
            <div className="form-group">
              <label>Reason for rejection <span style={{ color: '#e53935' }}>*</span></label>
              <textarea
                value={rejectNote}
                onChange={(e) => setRejectNote(e.target.value)}
                placeholder="Enter the reason for rejecting this registration..."
                rows={3}
                style={{ width: '100%', resize: 'vertical' }}
              />
            </div>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 16 }}>
              <button className="btn btn-outline" onClick={() => setShowRejectModal(null)}>Cancel</button>
              <button
                className="btn"
                onClick={() => handleReject(showRejectModal)}
                disabled={actionLoading || !rejectNote.trim()}
                style={{ background: '#f44336', color: '#fff', border: 'none' }}
              >
                {actionLoading ? 'Rejecting...' : 'Confirm Rejection'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function DetailField({ label, value, isComponent }) {
  return (
    <div>
      <div style={{ fontSize: 12, color: '#888', marginBottom: 2 }}>{label}</div>
      <div style={{ fontSize: 14, fontWeight: 500 }}>{isComponent ? value : <span>{value}</span>}</div>
    </div>
  );
}

const thStyle = { padding: '10px 12px', fontSize: 13, fontWeight: 600, borderBottom: '2px solid #e0e0e0' };
const overlayStyle = {
  position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
  background: 'rgba(0,0,0,0.5)', display: 'flex',
  alignItems: 'center', justifyContent: 'center', zIndex: 1000,
};

const modalStyle = {
  background: '#fff', borderRadius: 12, padding: '24px',
  maxWidth: 650, width: '90%', maxHeight: '85vh', overflowY: 'auto',
  boxShadow: '0 4px 24px rgba(0,0,0,0.15)',
};
